import { getLabelValueColumn, getStatusDesColumn, renderColumn } from 'components/TableColumn/TableColumn';
import { PartnershipMode } from 'core/agency/Agency';
import { formatPercentageText, RevenueSharing } from 'core/revenueSharing/RevenueSharing';
import { DefaultRevenueSharingManager, RevenueSharingManager } from 'core/revenueSharing/RevenueSharingManager';
import { getPartnershipModeLabel } from 'helper/PartnershipModeHelper';
import { useCallAPI } from 'hooks/useCallAPI';
import { useCallback, useEffect, useMemo, useState } from 'react';
import i18n from 'i18n';
import styles from './revenueSharingList.module.scss';

export enum RevenueSharingColumn {
  PURPOSE = 'purpose',
  PARTNERSHIP_MODE = 'partnershipModeId',
  FINISHED = 'finished',
  EFFECTIVE_DATE = 'effectiveDate',
  ADJUSTMENTS = 'adjustments',
  CREATOR = 'creator'
}

const defaultRevenueSharingManager: RevenueSharingManager = new DefaultRevenueSharingManager();
export const useRevenueSharingListModel = () => {

  const { loading, callAPIs } = useCallAPI();
  const [revenueSharings, setRevenueSharings] = useState<RevenueSharing[]>([]);
  const [partnershipModeId, setPartnershipModeId] = useState<PartnershipMode>(PartnershipMode.SELF_SERVING);

  useEffect(() => {
    callAPIs(
      [() => defaultRevenueSharingManager.getRevenueSharings()],
      (results: RevenueSharing[]) => {
        setRevenueSharings(results);
      }
    );
  }, [callAPIs]);

  // Nav 的 eventKey 一律是字串，PartnershipMode 是數字 enum，不轉型會 filter 不到任何資料
  const selectPartnershipMode = useCallback((tabName: string) => {
    setPartnershipModeId(Number(tabName));
  }, []);

  const getColumnDefinition = useCallback((column: RevenueSharingColumn) => {
    return {
      dataField: column,
      text: i18n.t<string>(`revenueSharingList.headers.${column}`),
      classes: styles[column]
    };
  }, []);

  const columns = useMemo(() => {
    return [
      renderColumn(
        getColumnDefinition(RevenueSharingColumn.PURPOSE),
        (value: string | null) => value ?? i18n.t<string>('revenueSharingList.labels.defaultPurpose')
      ),
      renderColumn(
        getColumnDefinition(RevenueSharingColumn.PARTNERSHIP_MODE),
        getPartnershipModeLabel
      ),
      getStatusDesColumn(
        getColumnDefinition(RevenueSharingColumn.FINISHED),
        (record: RevenueSharing) => {
          return record.finished
            ? {
              des: i18n.t<string>('revenueSharingList.labels.finished'),
              color: 'black'
            }
            : {
              des: i18n.t<string>('revenueSharingList.labels.preparing'),
              color: 'light'
            };
        }
      ),
      getColumnDefinition(RevenueSharingColumn.EFFECTIVE_DATE),
      getLabelValueColumn(
        getColumnDefinition(RevenueSharingColumn.ADJUSTMENTS),
        (record: RevenueSharing) => ([{
          label: i18n.t<string>('revenueSharingList.labels.adjustmentItem'),
          value: i18n.t<string>('revenueSharingList.labels.revenueSharing')
        }, {
          label: i18n.t<string>('revenueSharingList.labels.percentage'),
          value: formatPercentageText(record.percentage)
        }])
      ),
      getColumnDefinition(RevenueSharingColumn.CREATOR)
    ];
  }, [getColumnDefinition]);

  const filteredRevenueSharings = useMemo(() => {
    return revenueSharings.filter(revenueSharing => revenueSharing.partnershipModeId === partnershipModeId);
  }, [revenueSharings, partnershipModeId]);

  return {
    loading,
    columns,
    partnershipModeId,
    filteredRevenueSharings,
    selectPartnershipMode
  };
};
